import React, { useEffect, useRef } from 'react';

export type MealFrequency = 'full' | 'light';

// Razorpay-hosted checkout (Subscription Buttons for monthly plans, Payment
// Pages for the 3-day trial) — shared between the in-app CheckoutPage and
// the public marketing MealPlansPage so both use the exact same doc links
// regardless of whether the visitor is logged in. See razorpay_service.py's
// webhook handlers, which provision the subscription after payment.
export const SUBSCRIPTION_BUTTON_IDS: Record<string, Record<MealFrequency, string | undefined>> = {
  basic: {
    full: import.meta.env.VITE_RAZORPAY_SUBBUTTON_BASIC_FULL,
    light: import.meta.env.VITE_RAZORPAY_SUBBUTTON_BASIC_LIGHT,
  },
  premium: {
    full: import.meta.env.VITE_RAZORPAY_SUBBUTTON_PREMIUM_FULL,
    light: import.meta.env.VITE_RAZORPAY_SUBBUTTON_PREMIUM_LIGHT,
  },
  elite: {
    full: import.meta.env.VITE_RAZORPAY_SUBBUTTON_ELITE_FULL,
    light: import.meta.env.VITE_RAZORPAY_SUBBUTTON_ELITE_LIGHT,
  },
};

// Trials are not tier-specific (see the doc) — just meal frequency.
export const TRIAL_PAGE_URLS: Record<MealFrequency, string | undefined> = {
  light: import.meta.env.VITE_RAZORPAY_TRIAL_LIGHT_PAGE_URL,
  full: import.meta.env.VITE_RAZORPAY_TRIAL_FULL_PAGE_URL,
};
export const TRIAL_FLAT_PRICE: Record<MealFrequency, number> = { light: 945, full: 1890 };

/** Renders a Razorpay Subscription Button by injecting its embed <script>
 * into a <form> — React can't execute a script tag from JSX/innerHTML
 * directly, so the script element is created and appended imperatively. */
export const RazorpaySubscribeButton: React.FC<{ buttonId: string }> = ({ buttonId }) => {
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    form.innerHTML = '';
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/payment-button.js';
    script.async = true;
    script.setAttribute('data-payment_button_id', buttonId);
    form.appendChild(script);
  }, [buttonId]);

  return <form ref={formRef} />;
};

/** Appends prefill query params to a Payment Page's long URL — only
 * meaningful when we already know the visitor's details (a logged-in
 * user); omitted fields are simply left for the visitor to type in. */
export function buildPrefilledTrialUrl(baseUrl: string, name?: string, email?: string, phone?: string): string {
  const params = new URLSearchParams();
  if (name) params.set('full_name', name);
  if (email) params.set('email', email);
  if (phone) params.set('phone', phone.replace(/^\+91/, ''));
  const query = params.toString();
  return query ? `${baseUrl}?${query}` : baseUrl;
}
